/**
 * Cycles service labels on case study cards using ScrambleTextPlugin.
 */
import gsap from "gsap";
import ScrambleTextPlugin from "gsap/ScrambleTextPlugin";

gsap.registerPlugin(ScrambleTextPlugin);

const tagContexts = new WeakMap();

const SCRAMBLE_SECONDS = 0.85;
/** Random pause between scrambles so cards stay out of sync (seconds). */
const HOLD_MIN = 2;
const HOLD_MAX = 5;
/** Stagger first scramble per card on load. */
const INITIAL_DELAY_MAX = 3.5;

function randomHoldSeconds() {
	return HOLD_MIN + Math.random() * (HOLD_MAX - HOLD_MIN);
}

function queryContainers(scopeRoot) {
	if (!scopeRoot || scopeRoot === document) {
		return document.querySelectorAll(".case-study-card__tags");
	}
	return scopeRoot.querySelectorAll(".case-study-card__tags");
}

/** Stop tag animations inside a subtree (e.g. before replacing archive grid HTML). */
export function revertCaseStudyCardTags(scopeRoot) {
	queryContainers(scopeRoot).forEach((container) => {
		const ctx = tagContexts.get(container);
		if (ctx) {
			ctx.revert();
			tagContexts.delete(container);
		}
	});
}

function parseLabels(container) {
	const raw = container.getAttribute("data-services");
	if (!raw) {
		return null;
	}
	try {
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed : null;
	} catch {
		return null;
	}
}

export function initCaseStudyCardTags(scopeRoot = document) {
	const prefersReduced =
		typeof window !== "undefined" &&
		window.matchMedia("(prefers-reduced-motion: reduce)").matches;

	queryContainers(scopeRoot).forEach((container) => {
		tagContexts.get(container)?.revert();

		const labels = parseLabels(container);
		if (!labels || labels.length <= 1 || prefersReduced) {
			return;
		}

		const tagEl = container.querySelector(".case-study-card__tag");
		if (!tagEl) {
			return;
		}

		const ctx = gsap.context(() => {
			let index = 0;

			function advance() {
				index = (index + 1) % labels.length;
				gsap.to(tagEl, {
					duration: SCRAMBLE_SECONDS,
					ease: "none",
					scrambleText: {
						text: labels[index],
						chars: "upperAndLowerCase",
						speed: 0.55,
					},
					onComplete: () => {
						gsap.delayedCall(randomHoldSeconds(), advance);
					},
				});
			}

			const firstAfter = Math.random() * INITIAL_DELAY_MAX;
			gsap.delayedCall(firstAfter, advance);
		}, container);

		tagContexts.set(container, ctx);
	});
}
